import { Inject, Injectable } from '@angular/core';
import { APPLICATION_SUITE_TOKEN, ErrorDCR } from '@ddt-web/config';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { ApiService, PreferenceChildData } from '@ddt-web/data-service';
import { ErrorObject } from '@ddt-web/error-handler';
import { FundPreferenceService } from '@ddt-web/fund-preferences';
import { Store } from '@ngrx/store';
import * as fromPairs from 'lodash/fromPairs';
import * as isEmpty from 'lodash/isEmpty';
import * as partition from 'lodash/partition';
import { Observable } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';

import { FUND_TAGS_STORE } from '../constants/fund-tag-constants';
import { FundTagData, FundTags } from '../models/fund-tags.interface';
import { FundTagStoreService } from './fund-tag-store.service';

@Injectable({ providedIn: 'root' })
export class FundTagService extends FundPreferenceService {
	@InjectConfig(SharedConfigKey.MAXIMUM_TAGS_PER_FUND) private readonly maxTagsPerFund: number = 10;
	@InjectConfig(SharedConfigKey.MAXIMUM_TAGGED_FUNDS) private readonly maxTaggedFunds: number = 250;

	private readonly preferenceKey: string;

	protected override readonly errorDcrKeys = {
		apiFailure: ErrorDCR.FUND_TAG_API_FAILURE_ERROR,
		fundPreferencePerFundLimitExceeded: ErrorDCR.FUND_TAG_TAGGED_FUND_LIMIT_EXCEEDED_ERROR
	};

	constructor(
		@Inject(APPLICATION_SUITE_TOKEN) private applicationSuite: string,
		protected apiService: ApiService,
		protected fundTagStoreService: FundTagStoreService,
		protected store: Store
	) {
		super(apiService, fundTagStoreService, store);
		this.preferenceKey = `${this.applicationSuite.toUpperCase()}_FUND_TAGS`;
	}

	public getAllFundTags(): Observable<FundTagData[]> {
		return this.getAllFundPreference(this.preferenceKey, 'fundTags');
	}

	public setFundTags(tagsToSet: FundTags): Observable<FundTagData[]> {
		let tagsToSetEntries: [string, FundTagData][] = Object.entries(tagsToSet);

		const fundsMissingClientId: FundTagData[] = tagsToSetEntries.filter(([id]: [string, FundTagData]) => !id).map(entry => entry?.[1]);
		const fundsExceedingLimit: FundTagData[] = Object.values(tagsToSet).filter(fundTags => fundTags.tags?.length > this.maxTagsPerFund) ?? [];
		const fundsWithIncompleteIdentifiers: FundTagData[] = Object.values(tagsToSet).filter(fundTags => !fundTags.cusip || !fundTags.morningstarSecId) ?? [];

		tagsToSetEntries = tagsToSetEntries
			.filter(([id]: [string, FundTagData]) => !!id)
			.filter(([, tagData]: [string, FundTagData]) => !fundsExceedingLimit.includes(tagData))
			.filter(([, tagData]: [string, FundTagData]) => !fundsMissingClientId.includes(tagData));

		return this.fundTagStoreService.getAllFundTags().pipe(
			take(1),
			switchMap((fundTagEntries: FundTags) => {
				const [tagsToUpdate, tagsToAdd]: [FundTags, FundTags] = partition(tagsToSetEntries, ([clientId]: [string, FundTagData]) => !!fundTagEntries[clientId]).map(
					fromPairs
				);
				const [tagsToRemove, tagsToEdit]: [FundTags, FundTags] = partition(Object.entries(tagsToUpdate), ([, fundTagData]: [string, FundTagData]) =>
					isEmpty(fundTagData.tags)
				).map(fromPairs);

				const setPreferences$ = this.setPreferences<FundTagData, FundTags>(
					fundTagEntries,
					tagsToAdd,
					tagsToRemove,
					tagsToEdit,
					FUND_TAGS_STORE,
					this.preferenceKey,
					this.applicationSuite,
					this.maxTaggedFunds
				);

				return <Observable<FundTagData[]>>setPreferences$.pipe(
					tap(() => {
						if (fundsExceedingLimit.length) {
							throw new ErrorObject({ fundTags: fundsExceedingLimit }, ErrorDCR.FUND_TAG_LIMIT_EXCEEDED_ERROR);
						}
						if (fundsMissingClientId.length) {
							throw new ErrorObject({ fundTags: fundsMissingClientId }, ErrorDCR.FUND_TAG_MISSING_ID_ERROR);
						}
						const persistedFundTagDataWithIncompleteIdentifiers = fundsWithIncompleteIdentifiers.filter(
							tagData => !Object.keys(tagsToRemove).includes(tagData.clientId)
						);
						if (persistedFundTagDataWithIncompleteIdentifiers.length) {
							throw new ErrorObject({ fundTags: persistedFundTagDataWithIncompleteIdentifiers }, ErrorDCR.FUND_TAG_MISSING_SECONDARY_IDENTIFIERS_ERROR);
						}
					})
				);
			})
		);
	}

	protected override translatePreferenceChildren(preferenceChildren: PreferenceChildData[]): FundTagData[] {
		if (isEmpty(preferenceChildren)) {
			return [];
		}

		return preferenceChildren
			.filter((preferenceChild: PreferenceChildData) => !!preferenceChild && !!preferenceChild.uniqueId)
			.map((preferenceChild: PreferenceChildData) => ({
				id: preferenceChild.id,
				clientId: preferenceChild.clientId || preferenceChild.id,
				uniqueId: preferenceChild.uniqueId,
				tags: preferenceChild.tags,
				ticker: preferenceChild.ticker,
				morningstarSecId: preferenceChild.morningstarSecId,
				cusip: preferenceChild.cusip,
				isBlkFund: preferenceChild.isBlkFund
			}));
	}
}
